import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import type { VirtualElement } from '@popperjs/core';
import { throttle, debounce } from '@ais-ui/utils';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';

// Define a type for screen position
interface ScreenPosition {
  x: number;
  y: number;
}

/**
 * Custom hook for positioning UI elements at specific map coordinates
 * Handles coordinate conversion, screen positioning, and popper integration
 *
 * @param {Object} map - OpenLayers map instance
 * @param {Array|null} mapCoordinate - Map coordinate [lon, lat] to position element at
 * @param {Object} popperOptions - Options to pass to usePopper
 * @returns {Object} Positioning utilities and state
 */
// Define a type for the Popper options based on what react-popper expects
interface PopperOptionsType {
  placement?:
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end';
  modifiers?: Array<{
    name: string;
    options?: Record<string, any>;
  }>;
  strategy?: 'absolute' | 'fixed';
}

export function useMapElementPositioning(
  mapCoordinate: number[] | null,
  popperOptions: Partial<PopperOptionsType> = {},
) {
  // State to track screen position (in pixels)
  const { map } = useOpenLayersContext();
  const [screenPosition, setScreenPosition] = useState<ScreenPosition | null>(
    null,
  );

  // Reference to the element we're positioning
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);

  // Store coordinate in ref for access in event handlers
  const coordinateRef = useRef(mapCoordinate);

  // Update the coordinate ref when prop changes
  useEffect(() => {
    coordinateRef.current = mapCoordinate;
  }, [mapCoordinate]);

  // Function to convert map coordinates to screen position
  const updateScreenPosition = useCallback(() => {
    if (!map || !coordinateRef.current) return;

    // Convert map coordinates to pixel coordinates
    const pixel = map.getPixelFromCoordinate(coordinateRef.current);

    if (pixel) {
      // Get the map container position
      const mapContainer = map.getTargetElement();
      const rect = mapContainer.getBoundingClientRect();

      // Calculate absolute screen position
      setScreenPosition({
        x: rect.left + pixel[0],
        y: rect.top + pixel[1],
      });
    }
  }, [map]);

  // Performance-optimized update functions
  const throttledUpdate = useMemo(
    () => throttle(updateScreenPosition, 16), // ~60fps
    [updateScreenPosition],
  );

  const debouncedUpdate = useMemo(
    () => debounce(updateScreenPosition, 100), // Final update
    [updateScreenPosition],
  );

  // Clean up throttle and debounce on unmount
  useEffect(() => {
    return () => {
      throttledUpdate.cancel();
      debouncedUpdate.cancel();
    };
  }, [throttledUpdate, debouncedUpdate]);

  // Set up map event listeners
  useEffect(() => {
    if (!map || !mapCoordinate) return;

    // Initial position calculation
    updateScreenPosition();

    // Use throttled updates during interaction for better performance
    map.on('pointerdrag', throttledUpdate);
    map.on('pointermove', throttledUpdate);
    map.on('postrender', throttledUpdate);

    // Use debounced update for the final position after interaction
    map.on('moveend', debouncedUpdate);
    map.on('change:size', debouncedUpdate);

    return () => {
      // Clean up event listeners
      map.un('pointerdrag', throttledUpdate);
      map.un('pointermove', throttledUpdate);
      map.un('postrender', throttledUpdate);
      map.un('moveend', debouncedUpdate);
      map.un('change:size', debouncedUpdate);
    };
  }, [
    map,
    mapCoordinate,
    updateScreenPosition,
    throttledUpdate,
    debouncedUpdate,
  ]);

  // Create virtual reference element for Popper
  const virtualReference: VirtualElement = useMemo(
    () => ({
      getBoundingClientRect: () => ({
        top: screenPosition?.y || 0,
        left: screenPosition?.x || 0,
        bottom: screenPosition?.y || 0,
        right: screenPosition?.x || 0,
        width: 0,
        height: 0,
        x: screenPosition?.x || 0,
        y: screenPosition?.y || 0,
        toJSON: () => ({}),
      }),
    }),
    [screenPosition],
  );

  // Default popper options
  const defaultPopperOptions: PopperOptionsType = {
    strategy: 'fixed',
    placement: 'right',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport',
          padding: 8,
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left', 'top', 'bottom'],
        },
      },
    ],
    ...popperOptions,
  };

  // Initialize popper
  const { styles, update } = usePopper(
    virtualReference,
    popperElement,
    defaultPopperOptions,
  );

  // Create throttled popper update function
  const throttledPopperUpdate = useMemo(
    () => (update ? throttle(update, 16) : null),
    [update],
  );

  // Clean up throttled popper update on unmount
  useEffect(() => {
    return () => {
      if (throttledPopperUpdate) {
        throttledPopperUpdate.cancel();
      }
    };
  }, [throttledPopperUpdate]);

  // Update popper when screen position changes
  useEffect(() => {
    if (throttledPopperUpdate && screenPosition) {
      throttledPopperUpdate();
    }
  }, [screenPosition, throttledPopperUpdate]);

  // Check if coordinate is in current viewport
  const isInViewport = useMemo(() => {
    if (!map || !mapCoordinate) return false;

    const mapExtent = map.getView().calculateExtent(map.getSize());
    return (
      mapCoordinate[0] >= mapExtent[0] &&
      mapCoordinate[0] <= mapExtent[2] &&
      mapCoordinate[1] >= mapExtent[1] &&
      mapCoordinate[1] <= mapExtent[3]
    );
  }, [map, mapCoordinate]);

  return {
    // Props for the element
    setPopperElement,
    popperElement,

    // Position data and styles
    screenPosition,
    styles: styles.popper,

    // Visibility state
    isInViewport,

    // Manual update function
    updatePosition: updateScreenPosition,
  };
}
